import React from 'react';
import {
  Box, Typography, Paper, List, ListItem, ListItemText, ListItemIcon,
  Avatar, useTheme, Divider, Card, CardActionArea
} from '@mui/material';
import {
  Business, People, Category, CalendarMonth,
  Message, Notifications, Extension, Payment, Chat,
  Analytics, Logout, ChevronRight, Person, Timer, EventNote, Engineering, ManageAccounts
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { setViewMode, clearViewMode } from '../../utils/viewMode';

const MoreScreen = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout, effectiveRole, actualRole } = useAuth();
  const isWorker = effectiveRole === 'worker';
  const isManagerAccount = actualRole === 'manager' || actualRole === 'admin';
  const canFieldView = isManagerAccount && !!user?.employee_id;

  const switchToField = () => { setViewMode('worker'); navigate('/worker'); };
  const switchToManager = () => { clearViewMode(); window.location.href = '/map/planner'; };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const managerSections = [
    {
      title: 'Tools',
      items: [
        { label: 'AI Chat', icon: <Chat />, path: '/chat', color: theme.palette.primary.main, bgcolor: theme.palette.primary.light + '20' },
        { label: 'Analytics', icon: <Analytics />, path: '/analytics', color: theme.palette.info.main, bgcolor: theme.palette.info.light + '20' }
      ]
    },
    {
      title: 'Management',
      items: [
        { label: 'Issues', icon: <Notifications />, path: '/jobs/issues', color: theme.palette.error.main, bgcolor: theme.palette.error.light + '20' },
        { label: 'Clients', icon: <People />, path: '/clients', color: theme.palette.secondary.main, bgcolor: theme.palette.secondary.light + '20' },
        { label: 'Team Overview', icon: <People />, path: '/team', color: theme.palette.primary.main, bgcolor: theme.palette.primary.light + '20' },
        { label: 'Timesheets', icon: <Timer />, path: '/team/timesheets', color: theme.palette.info.main, bgcolor: theme.palette.info.light + '20' },
        { label: 'Leave Management', icon: <EventNote />, path: '/team/leave', color: theme.palette.warning.main, bgcolor: theme.palette.warning.light + '20' }
      ]
    },
    {
      title: 'Settings',
      items: [
        { label: 'Business Profile', icon: <Business />, path: '/settings/business' },
        { label: 'Users & Roles', icon: <Person />, path: '/settings/users' },
        { label: 'Job Types', icon: <Category />, path: '/settings/job-types' },
        { label: 'Scheduling', icon: <CalendarMonth />, path: '/settings/scheduling' },
        { label: 'Templates', icon: <Message />, path: '/settings/templates' },
        { label: 'Notifications', icon: <Notifications />, path: '/settings/notifications' },
        { label: 'Integrations', icon: <Extension />, path: '/settings/integrations' },
        { label: 'Billing', icon: <Payment />, path: '/settings/billing' }
      ]
    }
  ];

  const workerSections = [
    {
      title: 'My Account',
      items: [
        { label: 'My Timesheet', icon: <Timer />, path: '/worker/time', color: theme.palette.info.main, bgcolor: theme.palette.info.light + '20' },
        { label: 'Request Leave', icon: <EventNote />, path: '/leave', color: theme.palette.warning.main, bgcolor: theme.palette.warning.light + '20' }
      ]
    },
    {
      title: 'Settings',
      items: [
        { label: 'Notifications', icon: <Notifications />, path: '/settings/notifications' }
      ]
    }
  ];

  // Managers linked to an employee record can run the field app too
  if (isWorker && isManagerAccount) {
    workerSections[0].items.unshift({
      label: 'Switch to Manager View', icon: <ManageAccounts />, onClick: switchToManager,
      color: theme.palette.secondary.main, bgcolor: theme.palette.secondary.light + '20'
    });
  }
  if (!isWorker && canFieldView) {
    managerSections[0].items.unshift({
      label: 'Field View (Worker App)', icon: <Engineering />, onClick: switchToField,
      color: theme.palette.success.main, bgcolor: theme.palette.success.light + '20'
    });
  }

  const sections = isWorker ? workerSections : managerSections;

  return (
    <Box sx={{ pb: 10 }}>
      {/* Profile Card */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 4, 
          border: '1px solid #E2E8F0', 
          borderRadius: 3,
          backgroundImage: 'linear-gradient(to right bottom, #ffffff, #F8FAFC)'
        }}
      >
        <CardActionArea sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <Avatar 
            sx={{ 
                width: 72, 
                height: 72, 
                bgcolor: 'primary.main',
                fontSize: '1.75rem',
                fontWeight: 700,
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
                mr: 2.5
            }}
            >
            {user?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
            <Box>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2, mb: 0.5 }}>
                {user?.full_name || user?.email || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                {user?.role || 'Manager'} • {user?.organization || 'Organization'}
            </Typography>
            </Box>
        </CardActionArea>
      </Card>

      {/* Sections */}
      {sections.map((section, idx) => (
        <Box key={idx} sx={{ mb: 4 }}>
          <Typography 
            variant="caption" 
            fontWeight={700} 
            color="text.secondary" 
            sx={{ px: 1, mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {section.title}
          </Typography>
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
            <List disablePadding>
              {section.items.map((item, itemIdx) => (
                <React.Fragment key={itemIdx}>
                  {itemIdx > 0 && <Divider variant="middle" sx={{ borderColor: '#F1F5F9' }} />}
                  <ListItem
                    button
                    onClick={() => (item.onClick ? item.onClick() : navigate(item.path))}
                    sx={{ py: 2, '&:hover': { bgcolor: '#F8FAFC' } }}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                        <Avatar 
                            variant="rounded"
                            sx={{ 
                                width: 32, 
                                height: 32, 
                                bgcolor: item.bgcolor || '#F1F5F9',
                                color: item.color || 'text.secondary'
                            }}
                        >
                            {React.cloneElement(item.icon, { fontSize: 'small' })}
                        </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label} 
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                    />
                    <ChevronRight color="action" fontSize="small" sx={{ opacity: 0.5 }} />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      ))}

      {/* Logout */}
      <Paper elevation={0} sx={{ border: '1px solid #FECACA', borderRadius: 3, overflow: 'hidden', mb: 4, bgcolor: '#FEF2F2' }}>
        <List disablePadding>
          <ListItem button onClick={handleLogout} sx={{ py: 2 }}>
            <ListItemIcon sx={{ minWidth: 48 }}>
                <Avatar 
                    variant="rounded"
                    sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: '#FEE2E2',
                        color: 'error.main'
                    }}
                >
                    <Logout fontSize="small" />
                </Avatar>
            </ListItemIcon>
            <ListItemText 
              primary="Log Out" 
              primaryTypographyProps={{ color: 'error.main', fontWeight: 700 }}
            />
          </ListItem>
        </List>
      </Paper>
      
      <Box sx={{ textAlign: 'center', pb: 4 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Soil Testing Agent v1.0.0
        </Typography>
      </Box>
    </Box>
  );
};

export default MoreScreen;
